import type {} from '@redux-devtools/extension'; // required for devtools typing
import { UTCDate } from '@date-fns/utc';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

type FiltersStore<T> = T & {
  getFilters: () => T;
  getFilter: <K extends keyof T>(filter: K) => T[K];
  setFilter: <K extends keyof T>(filter: K, value: T[K]) => void;
  setFilters: <K extends keyof T>(filters: { [F in K]: T[F] }) => void;
  resetFilter: <K extends keyof T>(filter: K) => void;
  resetAllFilters: () => void;
  hasActiveFilters: (keys?: (keyof T)[]) => boolean;
};

export function createFilterStore<T extends object>(initialData: T) {
  return create<FiltersStore<T>>()(
    devtools(
      immer((set, get) => ({
        ...initialData,
        getFilters: () => get(),
        getFilter: (filter) => get()[filter],
        setFilter: (filter, value) =>
          set({ [filter]: value } as unknown as Partial<FiltersStore<T>>),
        setFilters: (filters) =>
          set((state) => {
            Object.assign(state, filters);
          }),
        resetFilter: (filter) =>
          set({ [filter]: initialData[filter] } as unknown as Partial<FiltersStore<T>>),
        resetAllFilters: () => set(initialData),
        hasActiveFilters: (keys) => {
          const current = get() as Record<string, unknown>;
          const initial = initialData as Record<string, unknown>;
          const keysToCheck = (keys as string[] | undefined) ?? Object.keys(initial);
          return keysToCheck.some((key) => {
            const currentValue = current[key];
            const initialValue = initial[key];
            if (currentValue instanceof UTCDate) return false;
            if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
              return (
                currentValue.length !== initialValue.length ||
                currentValue.some((v, i) => v !== initialValue[i])
              );
            }
            return currentValue !== initialValue;
          });
        },
      })),
    ),
  );
}
